import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import CheckMyPriceModal from "../components/CheckMyPriceModal";
import Logo from "../components/Logo";
import { LangToggle, useLang } from "../lib/i18n";
import { getCart } from "../lib/cart";
import { getContent } from "../lib/content";

function NavBar({ cartCount, c }) {
  return (
    <nav className="hp-nav" data-testid="hp-nav">
      <Logo size="md" linkTo="/" testId="nav-logo" />
      <ul className="hp-nav-links">
        <li><Link to="/tirzepatide">{c.nav.tirzepatide}</Link></li>
        <li><Link to="/nad">{c.nav.nadPlus}</Link></li>
        <li><Link to="/sermorelin">{c.nav.sermorelin}</Link></li>
        <li><a href="#guides">{c.nav.guides}</a></li>
        <li><a href="#contact">{c.nav.contact}</a></li>
        <li><Link to="/estore">{c.nav.shop}</Link></li>
      </ul>
      <div className="hp-nav-right">
        <LangToggle />
        <Link to="/cart" className="hp-cart" aria-label="Cart" data-testid="nav-cart">
          🛒{cartCount > 0 && <span className="hp-cart-badge">{cartCount}</span>}
        </Link>
      </div>
    </nav>
  );
}

function Hero({ c }) {
  return (
    <section className="hp-hero" data-testid="hero">
      <div className="hp-hero-text">
        <div className="hp-eyebrow">{c.hero.eyebrow}</div>
        <h1 className="hp-h1">{c.hero.h1_pre}<em>{c.hero.h1_em}</em>{c.hero.h1_post}</h1>
        <p className="hp-sub">{c.hero.sub}</p>
        <Link to="/intake/start" className="hp-btn-primary" data-testid="hero-cta">{c.hero.cta}</Link>
        <div className="hp-trust-line">{c.hero.trust.map((t,i)=>(<span key={i}>✔ {t}</span>))}</div>
      </div>
      <div className="hp-hero-img">
        <img src="https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69db6b26982fd67a358820db.png" alt="Medical Weight Loss Miami — Antiaging by BB" />
      </div>
    </section>
  );
}

function HeroStats({ c }) {
  return (
    <section className="hp-stats" data-testid="hero-stats">
      {c.stats.map((s,i)=>(
        <div key={i} className="hp-stat"><div className="hp-stat-v">{s.v}</div><div className="hp-stat-l">{s.l}</div></div>
      ))}
    </section>
  );
}

function Ticker({ c }) {
  return (
    <div className="hp-ticker" data-testid="hp-ticker">
      <div className="hp-ticker-track">
        {[...c.ticker, ...c.ticker, ...c.ticker].map((t, i) => (
          <span key={i} className="hp-ticker-item">{t}<span className="hp-ticker-sep">✦</span></span>
        ))}
      </div>
    </div>
  );
}

function Problem({ c }) {
  return (
    <section id="problem" className="hp-section" data-testid="hp-problem">
      <div className="hp-eyebrow">{c.problem.eyebrow}</div>
      <h2 className="hp-h2">{c.problem.h2_pre}<em>{c.problem.h2_em}</em></h2>
      <ul className="hp-bullets">{c.problem.bullets.map((b,i)=><li key={i}>{b}</li>)}</ul>
      <p className="hp-closing">
        {c.problem.closing_pre}<br/>{c.problem.closing_mid}<br/><strong>{c.problem.closing_post}</strong>
      </p>
    </section>
  );
}

function Solution({ c }) {
  return (
    <section id="solution" className="hp-section" data-testid="hp-solution">
      <div className="hp-eyebrow">{c.solution.eyebrow}</div>
      <h2 className="hp-h2">{c.solution.h2_pre}<em>{c.solution.h2_em}</em></h2>
      <p className="hp-subhead">{c.solution.sub}</p>
      <div className="hp-grid-4">
        {c.solution.cards.map((card, i) => (
          <div key={i} className="hp-soln-card">
            <h3 className="hp-soln-title">{card.t}</h3>
            <p className="hp-soln-desc">{card.d}</p>
          </div>
        ))}
      </div>
      <p className="hp-closing">{c.solution.closing}</p>
    </section>
  );
}

function MainOffer({ c }) {
  return (
    <section id="main-offer" className="hp-section hp-offer" data-testid="hp-main-offer">
      <div className="hp-offer-img">
        <img src="https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f3554514daed238d4b4c7b.png" alt="Tirzepatide Weight Loss Program" />
      </div>
      <div className="hp-offer-body">
        <div className="hp-eyebrow">{c.mainOffer.eyebrow}</div>
        <h2 className="hp-h2">{c.mainOffer.h2}</h2>
        <span className="hp-badge">{c.mainOffer.badge}</span>
        <p className="hp-tagline">{c.mainOffer.tagline}</p>
        <div className="hp-pills">{c.mainOffer.pills.map((p,i)=><span key={i} className="hp-pill">{p}</span>)}</div>
        <div className="hp-cta-row">
          <Link to="/intake/start" className="hp-btn-primary" data-testid="tirz-cta">{c.mainOffer.cta}</Link>
          <a href="https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/6a28a0d2bb09e3b139a7609d.pdf" target="_blank" rel="noreferrer" className="hp-btn-ghost">{c.mainOffer.guide}</a>
        </div>
      </div>
    </section>
  );
}

function AntiAgingSupport({ c }) {
  return (
    <section id="support" className="hp-section" data-testid="hp-support">
      <div className="hp-eyebrow">{c.support.eyebrow}</div>
      <h2 className="hp-h2">{c.support.h2_pre}<em>{c.support.h2_em}</em></h2>
      <p className="hp-subhead">{c.support.sub}</p>
      <div className="hp-grid-2">
        <article className="hp-support-card">
          <img src="https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f3554514daed238d4b4c7c.png" alt="NAD+"/>
          <div className="hp-support-body">
            <div className="hp-eyebrow-sm">{c.support.nad.eyebrow}</div>
            <h3 className="hp-h3">{c.support.nad.title}</h3>
            <p className="hp-tagline">{c.support.nad.tagline}</p>
            <p className="hp-body">{c.support.nad.body}</p>
            <Link to="/intake/start?product=nad" className="hp-btn-primary">{c.support.nad.cta}</Link>
          </div>
        </article>
        <article className="hp-support-card">
          <img src="https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f355450549a7387e14d21f.png" alt="Sermorelin"/>
          <div className="hp-support-body">
            <div className="hp-eyebrow-sm">{c.support.serm.eyebrow}</div>
            <h3 className="hp-h3">{c.support.serm.title}</h3>
            <p className="hp-tagline">{c.support.serm.tagline}</p>
            <p className="hp-body">{c.support.serm.body}</p>
            <Link to="/intake/start?product=sermorelin" className="hp-btn-primary">{c.support.serm.cta}</Link>
          </div>
        </article>
      </div>
      <p className="hp-closing">{c.support.closing}</p>
    </section>
  );
}

function WhyItWorks({ c }) {
  return (
    <section id="why" className="hp-section" data-testid="hp-why">
      <div className="hp-eyebrow">{c.why.eyebrow}</div>
      <h2 className="hp-h2">{c.why.h2_pre}<em>{c.why.h2_em}</em></h2>
      <p className="hp-subhead">{c.why.sub}</p>
      <div className="hp-grid-3">
        {c.why.cards.map((card,i)=>(
          <div key={i} className="hp-why-card">
            <h3 className="hp-h3">{card.title}</h3>
            <p className="hp-body">{card.desc}</p>
          </div>
        ))}
      </div>
      <p className="hp-closing">{c.why.closing}</p>
    </section>
  );
}

function HowItWorks({ c }) {
  return (
    <section id="how" className="hp-section" data-testid="hp-how">
      <div className="hp-eyebrow">{c.how.eyebrow}</div>
      <h2 className="hp-h2">{c.how.h2_pre}<em>{c.how.h2_em}</em></h2>
      <p className="hp-subhead">{c.how.sub}</p>
      <ol className="hp-steps">
        {c.how.steps.map((s,i)=>(
          <li key={i} className="hp-step">
            <div className="hp-step-n">{String(i+1).padStart(2,"0")}</div>
            <div><h3 className="hp-h3">{s.t}</h3><p className="hp-body">{s.d}</p></div>
          </li>
        ))}
      </ol>
      <div className="hp-center"><Link to="/intake/start" className="hp-btn-primary">{c.how.cta}</Link></div>
    </section>
  );
}

function Pricing({ onCmp, c }) {
  return (
    <section id="pricing" className="hp-section hp-pricing" data-testid="hp-pricing">
      <div className="hp-eyebrow">{c.pricing.eyebrow}</div>
      <h2 className="hp-h1" style={{textAlign:"center", margin:"0 auto 14px"}}>
        {c.pricing.h1_pre}<span style={{color:"var(--gold)"}}>$347*</span>
      </h2>
      <p className="hp-subhead hp-center"><strong>{c.pricing.sub_strong}</strong>{c.pricing.sub_post}</p>
      <div className="hp-center" style={{marginTop:24}}>
        <button onClick={onCmp} className="hp-btn-primary" data-testid="pricing-check-my-price">{c.pricing.cta}</button>
      </div>
      <p className="hp-microcopy hp-center">{c.pricing.micro}</p>
    </section>
  );
}

function Trust({ c }) {
  const icons = ["⚕","🛰","✦","🔒"];
  return (
    <section id="trust" className="hp-section" data-testid="hp-trust">
      <div className="hp-eyebrow">{c.trust.eyebrow}</div>
      <h2 className="hp-h2">{c.trust.h2_pre}<em>{c.trust.h2_em}</em></h2>
      <div className="hp-grid-4">
        {c.trust.pillars.map((t,i)=>(
          <div key={i} className="hp-trust-card"><div className="hp-trust-icon">{icons[i]}</div><div className="hp-trust-title">{t}</div></div>
        ))}
      </div>
    </section>
  );
}

function FinalCTA({ c }) {
  return (
    <section className="hp-section hp-final-cta" data-testid="hp-final-cta">
      <h2 className="hp-h2">{c.finalCta.h2_pre}<em>{c.finalCta.h2_em}</em></h2>
      <p className="hp-subhead">{c.finalCta.sub}</p>
      <div className="hp-center"><Link to="/intake/start" className="hp-btn-primary">{c.finalCta.cta}</Link></div>
      <p className="hp-microcopy hp-center">{c.finalCta.micro}</p>
    </section>
  );
}

function FreeGuides({ c }) {
  const urls = [
    "https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/6a28a0d2bb09e3b139a7609d.pdf",
    "https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69db3f72b30bc52371116384.pdf",
    "https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69db4013019dc508d365bfd3.pdf",
  ];
  return (
    <section id="guides" className="hp-section" data-testid="hp-guides">
      <div className="hp-eyebrow">{c.guides.eyebrow}</div>
      <h2 className="hp-h2">{c.guides.h2_pre}<em>{c.guides.h2_em}</em>{c.guides.h2_post}</h2>
      <p className="hp-subhead">{c.guides.sub}</p>
      <div className="hp-grid-3">
        {c.guides.items.map((g,i)=>(
          <a key={i} href={urls[i]} target="_blank" rel="noreferrer" className="hp-guide-card">
            <div className="hp-guide-icon">{g.i}</div>
            <h3 className="hp-h3">{g.t}</h3>
            <span className="hp-guide-link">{c.guides.link}</span>
          </a>
        ))}
      </div>
    </section>
  );
}

function Reviews({ c }) {
  return (
    <section id="reviews" className="hp-section" data-testid="hp-reviews">
      <div className="hp-eyebrow">{c.reviews.eyebrow}</div>
      <h2 className="hp-h2">{c.reviews.h2_pre}<em>{c.reviews.h2_em}</em></h2>
      <div className="hp-grid-3">
        {c.reviews.items.map((r,i)=>(
          <div key={i} className="hp-review-card">
            <div className="hp-review-stars">★★★★★</div>
            <p className="hp-review-text">"{r.text}"</p>
            <div className="hp-review-author">{r.name}</div>
            <div className="hp-review-tag">{c.reviews.tag}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function HipaaBanner({ c }) {
  return (
    <div className="hp-hipaa">
      <span className="hp-hipaa-h">H</span>
      <span><strong>{c.hipaa.strong}</strong>{c.hipaa.text}</span>
    </div>
  );
}

function Footer({ c }) {
  return (
    <footer id="contact" className="hp-footer">
      <div className="hp-footer-grid">
        <div className="hp-footer-brand">
          <Logo size="lg" linkTo={null} testId="footer-logo"/>
          <p className="hp-footer-tagline">{c.footer.tagline}</p>
          <div className="hp-footer-socials">
            <a href="#" aria-label="Instagram">IG</a>
            <a href="#" aria-label="Facebook">FB</a>
            <a href="#" aria-label="TikTok">TK</a>
          </div>
        </div>
        <div>
          <h4 className="hp-footer-h">{c.footer.navH}</h4>
          <ul>
            {c.footer.navItems.map((it,i)=><li key={i}><a href={["#main-offer","#support","#how","#pricing","#guides"][i]}>{it}</a></li>)}
          </ul>
        </div>
        <div>
          <h4 className="hp-footer-h">{c.footer.gsH}</h4>
          <ul>
            <li><Link to="/estore">{c.footer.gsItems[0]}</Link></li>
            <li><Link to="/intake/start">{c.footer.gsItems[1]}</Link></li>
            <li><Link to="/estore">{c.footer.gsItems[2]}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="hp-footer-h">{c.footer.ctH}</h4>
          <ul>
            <li><a href="mailto:care@antiagingbybb.com">care@antiagingbybb.com</a></li>
            <li><a href="tel:+13055289525">+1 (305) 528 9525</a></li>
            <li>11098 Biscayne Blvd, Suite 401<br/>Miami FL 33161</li>
            <li>{c.footer.hours}</li>
          </ul>
        </div>
      </div>
      <div className="hp-footer-bottom">
        <span>{c.footer.copyright}</span>
        <span><a href="#">{c.footer.privacy}</a> | <a href="#">{c.footer.terms}</a></span>
      </div>
    </footer>
  );
}

export default function HomePage() {
  const [cmpOpen, setCmpOpen] = useState(false);
  const [cartCount, setCartCount] = useState(getCart().reduce((s,i)=>s+i.qty,0));
  const loc = useLocation();
  const { lang } = useLang();
  const c = getContent(lang);

  useEffect(() => {
    const fn = () => setCartCount(getCart().reduce((s,i)=>s+i.qty,0));
    window.addEventListener("cart-updated", fn);
    return () => window.removeEventListener("cart-updated", fn);
  }, []);

  useEffect(() => {
    if (loc.hash) {
      const el = document.querySelector(loc.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [loc.hash, loc.pathname]);

  return (
    <div className="hp-root" data-testid="home-page">
      <NavBar cartCount={cartCount} c={c} />
      <Hero c={c}/>
      <div className="hp-divider"/>
      <HeroStats c={c}/>
      <div className="hp-divider"/>
      <Ticker c={c}/>
      <div className="hp-divider"/>
      <Problem c={c}/>
      <div className="hp-divider"/>
      <Solution c={c}/>
      <div className="hp-divider"/>
      <MainOffer c={c}/>
      <div className="hp-divider"/>
      <AntiAgingSupport c={c}/>
      <div className="hp-divider"/>
      <WhyItWorks c={c}/>
      <div className="hp-divider"/>
      <HowItWorks c={c}/>
      <div className="hp-divider"/>
      <Pricing onCmp={()=>setCmpOpen(true)} c={c}/>
      <div className="hp-divider"/>
      <Trust c={c}/>
      <div className="hp-divider"/>
      <FinalCTA c={c}/>
      <div className="hp-divider"/>
      <FreeGuides c={c}/>
      <div className="hp-divider"/>
      <Reviews c={c}/>
      <div className="hp-divider"/>
      <HipaaBanner c={c}/>
      <Footer c={c}/>
      <CheckMyPriceModal open={cmpOpen} onClose={()=>setCmpOpen(false)} />
    </div>
  );
}

